import LanguageSelector from '../i18n/LanguageSelector';
import { useAppTranslation } from '../i18n/useAppTranslation';
import AuthLogo from '../components/AuthLogo';
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageMeta } from '../utils/usePageMeta';
import api from '../services/api';
import { Container, TextField, Button, Typography, Box, Alert, Paper, ToggleButton, ToggleButtonGroup, useTheme, useMediaQuery, InputAdornment, IconButton, Divider } from '@mui/material';
import { SportsScore, Person, ArrowBack, Visibility, VisibilityOff, Science } from '@mui/icons-material';

const Login = () => {
  const { tr } = useAppTranslation();
  usePageMeta('Connexion — G1Sport', 'Connectez-vous à votre espace G1Sport pour gérer vos séances, votre planning et votre messagerie.');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('SPORTIF');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRoleChange = (event: React.MouseEvent<HTMLElement>, newRole: string) => {
    if (newRole !== null) {
      setSelectedRole(newRole);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      // Clear any existing tokens before login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      const response = await api.post('/users/token/', {
        username,
        password,
      });
      
      const userData = await login(response.data.access, response.data.refresh);

      // Redirect based on actual user role (admin uses toggle)
      const role = userData?.role;
      if (role === 'COACH') {
        navigate('/coach/dashboard');
      } else if (role === 'SPORTIF') {
        navigate('/sportif/dashboard', { state: location.state?.coach ? { tab: 1, coach: location.state.coach } : undefined });
      } else if (role === 'ADMIN') {
        navigate(selectedRole === 'COACH' ? '/coach/dashboard' : '/sportif/dashboard');
      } else {
        navigate('/sportif/dashboard', { state: location.state?.coach ? { tab: 1, coach: location.state.coach } : undefined });
      }
    } catch (err: any) {
      console.error(err);
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        setError('Identifiants invalides. Veuillez vérifier votre nom d\'utilisateur et mot de passe.');
      } else {
        setError('Une erreur est survenue lors de la connexion. Veuillez réessayer.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', position: 'relative', px: isMobile ? 2 : 3, py: isMobile ? 2 : 0 }}>
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBack />}
        variant="outlined"
        size={isMobile ? "small" : "medium"}
        sx={{
          position: 'absolute',
          // Sous la zone sure (encoche/camera) pour rester cliquable sur mobile.
          top: isMobile ? 'calc(16px + env(safe-area-inset-top))' : 24,
          left: isMobile ? 16 : 24,
          fontWeight: 700,
          zIndex: 1200,
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'white',
          color: 'text.primary',
          borderColor: 'divider',
          backdropFilter: 'blur(20px)',
          boxShadow: (theme) => theme.palette.mode === 'light' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
          '&:hover': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.9)' : '#f8fafc',
            borderColor: 'primary.main',
            transform: 'translateY(-2px)'
          }
        }}
      >
         {tr("Retour")} </Button>
      <Box
        sx={{
          width: '100%',
          animation: 'fadeIn 0.8s ease-out',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          // Descend le contenu sous la barre d'etat/encoche + le bouton Retour.
          pt: isMobile ? 'calc(72px + env(safe-area-inset-top))' : 0
        }}
      >
        <Paper sx={{
          p: isMobile ? 3 : 5,
          backdropFilter: 'blur(20px)',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.05)',
          borderRadius: 4,
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.05)'
        }}>
<LanguageSelector/>

          <Box sx={{ textAlign: 'center', mb: 1, width: '100%' }}>
            <AuthLogo />
            <Typography variant={isMobile ? "h4" : "h3"} sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: '#FFD700',
              mb: 0
            }}>
              G1Sport
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" sx={{ fontWeight: 400, mb: 0 }}>
               {tr("Coaching sportif. Simple. Humain. Efficace.")} </Typography>
          </Box>
          <Typography color="text.secondary" align="center" sx={{ mb: 4, fontSize: isMobile ? '0.9rem' : '1rem' }}>
             {tr("Accède à ton espace coach ou sportif.")} </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={selectedRole}
              exclusive
              onChange={handleRoleChange}
              aria-label="profile selection"
              sx={{ 
                width: '100%',
                gap: isMobile ? 1 : 3,
                '& .MuiToggleButton-root': { 
                  flex: 1,
                  py: isMobile ? 1.5 : 2,
                  borderRadius: '12px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: isMobile ? '0.9rem' : '1rem'
                },
                '& .Mui-selected': {
                  bgcolor: 'primary.main !important',
                  color: 'white !important',
                  borderColor: 'primary.main !important',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              <ToggleButton value="SPORTIF">
                <Person sx={{ mr: 1 }} />
                 {tr("Sportif")} </ToggleButton>
              <ToggleButton value="COACH">
                <SportsScore sx={{ mr: 1 }} />
                 {tr("Coach")} </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{typeof error === 'string' ? tr(error) : error}  {tr("Si vous venez de vous inscrire, confirmez d’abord votre adresse e-mail.")}</Alert>}
            <Button component={Link} to="/confirmer-email" size="small" sx={{ mb: 1 }}>{tr("Confirmer mon adresse / Renvoyer le lien")}</Button>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={tr("Nom d'utilisateur")}
              name="username"
              autoComplete="username"
              autoFocus
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              size={isMobile ? "medium" : "medium"}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={tr("Mot de passe")}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                   {tr("Mot de passe oublié ?")} </Typography>
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 3, py: 1.5, fontSize: '1.1rem' }}
            >
               {tr("Se connecter")} </Button>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Link to="/register" state={{ role: selectedRole }} style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                   {tr("Pas de compte ? Créer un compte")} </Typography>
              </Link>
            </Box>

            {/* Entree directe en mode decouverte, sans inscription. */}
            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="text.secondary">{tr("ou")}</Typography>
            </Divider>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Science />}
              onClick={() => navigate(`/visiteur?profil=${selectedRole.toLowerCase()}`)}
              sx={{ py: 1.3, fontWeight: 700, borderRadius: 2 }}
            >
               {tr("Continuer en visiteur")} </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
